// @ts-nocheck
// remove this after full Pinia implementation

import { v4 } from "uuid";
import { omit } from "@/utils/utils";
import type { InputData } from "@/composables/forms/formTypes";
import { mergeDiffs } from "@/utils/diff/mergeDiffs";
import type {
  PiniaPluginContext,
  PiniaCustomProperties,
  PiniaCustomStateProperties
} from "pinia";
import "pinia";
import type { Maybe } from "gql/api";

import type { Router } from "vue-router";
import type { ComputedRef } from "vue";
import { computed, watch } from "vue";

// These fields will be removed from the form data
// during the prepare() method.
const omittedFields = [
  "__typename",
  "winning_revision_id",
  "winning_revision_author_id",
  "parent_id",
  "journal_author_id",
  "journal_created_at",
  "journal_updated_at",
  "journal_deleted_at",
  "winning_revision_created_at",
  "winning_revision_updated_at",
  "winning_revision_deleted_at",
  "winning_revision_parent_id",
  "author_id" // For deviation survey only
];

// These fields will not
// show up in the form diff.
const keysToOmitFromDiff = [
  "parent_id",
  "cloned_from_journal_id",
  "journal_created_at",
  "journal_updated_at",
  "winning_revision_created_at",
  "winning_revision_updated_at",
  "winning_revision_parent_id",
  (key: string) => key.startsWith("r_")
];

const transformData = (data: unknown): Record<string, unknown> => {
  if (typeof data !== "object" || data === null) return {};
  // Narrow for data that uses revision structure
  if ("winning_revision_id" in data) {
    const flatData = omit(
      data,
      /* @ts-ignore */ /* eslint-disable-line @typescript-eslint/ban-ts-comment */
      (value, key) =>
        // Omit table relationships and nested data
        (key as string).startsWith("r_") ||
        // Omits all fields specified in omittedFields variable
        omittedFields.includes(key as string)
    );
    return {
      ...flatData,
      id: v4(),
      journal_id: data.id || v4(),
      parent_revision_id: data.winning_revision_id,
      deleted_at: data.journal_deleted_at
    };
  } else {
    return omit(
      data,
      (v, k) =>
        // Omit table relationships and nested data
        (k as string).startsWith("r_") ||
        // Omits all fields specified in omittedFields variable
        omittedFields.includes(k as string)
    ) as Record<string, unknown>;
  }
};

export type FormStoreState<T> = {
  formData: T;
  initialData: T;
  requiredFields?: string[];
};

export type PiniaFormMethods<T> = {
  hasFormData: boolean;
  formDiff: Record<string, unknown>;
  preparedForm: Record<string, unknown>;
  preparedFormAsArray: Record<string, unknown>[];
  preparedDiffsAsArray: Record<string, unknown>[];
  missingFields: string[];
  invalidFields: { [key: string]: boolean };
  setData(data: T): void;
  updateForm(formUpdate: Partial<T>): T;
  resetForm(initialValue?: T): void;
};

export type PiniaFormStore<T> = FormStoreState<T> & PiniaFormMethods<T>;

export const formPlugin = function formPlugin<T>(
  c: PiniaPluginContext
): Partial<PiniaCustomProperties & PiniaCustomStateProperties> {
  const { store, options } = c;

  if (!options?.useForm) {
    return {};
  }

  const formDiff = computed(() => {
    return mergeDiffs(
      store.data as Maybe<Record<string, unknown>>,
      store.formData,
      keysToOmitFromDiff
    ) as Record<string, unknown>;
  });

  const missingFields = computed(() => {
    const { requiredFields } = store;
    const final =
      requiredFields?.filter((r: string) => {
        return !store.formData[r];
      }) || [];
    return final;
  });

  return {
    hasFormData: computed(() => {
      return store.formData && Object.keys(store.formData).length > 0;
    }),
    formDiff,
    preparedForm: computed(() => {
      return transformData(store.formData);
    }),
    preparedFormAsArray: computed(() => {
      return Object.keys(store.formData).map(key =>
        transformData(store.formData[key as keyof InputData])
      );
    }),
    preparedDiffsAsArray: computed(() => {
      return Object.keys(formDiff.value).map(key =>
        transformData(formDiff.value[key as keyof InputData])
      );
    }),
    missingFields,
    invalidFields: computed(() => {
      const fields: { [key: string]: boolean } = {};
      missingFields.value.forEach((f: string) => {
        fields[f] = true;
      });
      return fields;
    }),
    setData(v: T) {
      store.initialData = v;
      store.formData = v ? { ...v } : null;
    },
    updateForm(formUpdate: Partial<T>): T {
      store.formData = {
        ...store.formData,
        ...formUpdate
      };
      return store.form;
    },
    resetForm(toValue = {} as T) {
      store.formData = { ...toValue };
    }
  };
};

declare module "pinia" {
  export interface PiniaCustomProperties {
    invalidFields: ComputedRef<{ [key: string]: boolean }>;
    hasFormData: ComputedRef<boolean>;
    formDiff: ComputedRef<Record<string, unknown>>;
    preparedForm: ComputedRef<Record<string, unknown>>;
    preparedFormAsArray: ComputedRef<Record<string, unknown>[]>;
    preparedDiffsAsArray: ComputedRef<Record<string, unknown>[]>;
    missingFields: ComputedRef<string[]>;
    setData(data: unknown): void;
    updateForm(formUpdate: Partial<unknown>): unknown;
    resetForm(initialValue?: unknown): void;
  }
  export interface DefineStoreOptionsBase<S extends StateTree, Store> {
    useForm?: boolean;
  }
}
